import React from 'react';
import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LoadedContent,
} from '@occc/ui-design-system';
import { KioskAssetFragment, User_Role } from '@occc/graphql';
import { useIsUserPermittedTo, Feature, useAccessControlContext } from '@occc/utils-access-control';

import { AssetsListTableRow } from './assets-list.table-row';

const convertToApolloError = (error: Error): ApolloError => {
  return new ApolloError({
    errorMessage: error.message,
    graphQLErrors: [],
    networkError: null,
  });
};

type AssetsListTableProps = {
  assets?: KioskAssetFragment[];
  loading: boolean;
  error?: Error;
  paginationProps: IPaginationProps;
  onItemClick: (assetId: string) => void;
  onView: (assetId: string) => void;
  onEdit: (assetId: string) => void;
  onDelete: (assetId: string) => void;
};

export const AssetsListTable = React.memo<AssetsListTableProps>(
  ({ assets, loading, error, paginationProps, onItemClick, onView, onEdit, onDelete }) => {
    const isUserPermittedToEditAsset = useIsUserPermittedTo(Feature.TOUCHPOINT_ASSET_EDIT);
    const isUserPermittedToViewAsset = useIsUserPermittedTo(Feature.TOUCHPOINT_ASSET_VIEW);
    const isUserPermittedToDeleteAsset = useIsUserPermittedTo(Feature.TOUCHPOINT_ASSET_DELETE);
    const accessControlContext = useAccessControlContext();
    const isAdminUser = accessControlContext?.userRole === User_Role.Admin;

    const renderData = (items: KioskAssetFragment[]) => {
      const rows = items.map((asset) => (
        <AssetsListTableRow
          key={asset.id}
          asset={asset}
          actions={{
            onItemClick,
            onView,
            onEdit,
            onDelete,
          }}
          permissions={{
            canEdit: isUserPermittedToEditAsset,
            canView: isUserPermittedToViewAsset,
            canDelete: isUserPermittedToDeleteAsset,
          }}
          isAdminUser={isAdminUser}
        />
      ));

      return (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 600, md: 'auto' }, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '35%' }}>Name</TableCell>
                  <TableCell sx={{ width: '45%' }}>Description</TableCell>
                  <TableCell align='right' sx={{ width: 80 }}>
                    Image
                  </TableCell>
                  {isAdminUser && <TableCell align='right' sx={{ width: 56 }}></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>{rows}</TableBody>
            </Table>
          </Box>
          <Pagination {...paginationProps} />
        </>
      );
    };

    return (
      <LoadedContent<KioskAssetFragment>
        error={error ? convertToApolloError(error) : undefined}
        loading={loading}
        items={assets}
        renderData={renderData}
      />
    );
  },
);
